import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Package, Layers, Inbox, FileText, Plus, Trash2, Pencil, X, Loader2, ExternalLink,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "tools", label: "Software / Tools", icon: Package },
  { id: "services", label: "Services", icon: Layers },
  { id: "content", label: "Site Content", icon: FileText },
];

export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    if (!checking && !user) navigate("/admin");
  }, [user, checking, navigate]);

  if (checking || !user) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-klein" /></div>;
  }

  return (
    <div className="flex min-h-screen bg-zinc-50">
      {/* Sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-zinc-200 bg-white lg:flex">
        <div className="flex items-center gap-2.5 border-b border-zinc-200 px-6 py-5">
          <img src="/genesis-mark-v2.png" alt="Genesis IT Solution logo" className="h-7 w-auto" />
          <span className="font-heading text-base font-black tracking-tighter">Genesis Admin</span>
        </div>
        <nav className="flex-1 p-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`mb-1 flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                tab === t.id ? "bg-klein text-white" : "text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-zinc-200 p-3">
          <button onClick={() => navigate("/")} className="mb-1 flex w-full items-center gap-3 px-4 py-2.5 text-sm text-zinc-600 hover:bg-zinc-100">
            <ExternalLink className="h-4 w-4" /> View site
          </button>
          <button data-testid="admin-logout" onClick={async () => { await logout(); navigate("/admin"); }} className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-accentred hover:bg-zinc-100">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </aside>

      <div className="flex-1">
        {/* mobile tabs */}
        <div className="flex gap-1 overflow-x-auto border-b border-zinc-200 bg-white p-2 lg:hidden">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`whitespace-nowrap px-3 py-2 text-xs font-semibold ${tab === t.id ? "bg-klein text-white" : "text-zinc-600"}`}>{t.label}</button>
          ))}
          <button onClick={async () => { await logout(); navigate("/admin"); }} className="whitespace-nowrap px-3 py-2 text-xs font-semibold text-accentred">Log out</button>
        </div>

        <div className="p-5 lg:p-8">
          {tab === "leads" && <LeadsPanel />}
          {tab === "tools" && <ToolsPanel />}
          {tab === "services" && <ServicesPanel />}
          {tab === "content" && <ContentPanel />}
        </div>
      </div>
    </div>
  );
}

function Header({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex items-end justify-between">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">{title}</h1>
        <p className="mt-1 text-sm text-zinc-500">{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

/* ----------------------------- LEADS ----------------------------- */
function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = () => { api.get("/admin/leads").then((r) => setLeads(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    setLeads((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
    if (open && open.id === id) setOpen({ ...open, status });
  };
  const del = async (id) => { await api.delete(`/admin/leads/${id}`); setLeads((l) => l.filter((x) => x.id !== id)); setOpen(null); };

  const badge = (s) => ({ new: "bg-klein text-white", contacted: "bg-amber-500 text-white", closed: "bg-zinc-300 text-zinc-700" }[s] || "bg-zinc-200");

  if (loading) return <Loader2 className="h-6 w-6 animate-spin text-klein" />;

  return (
    <div>
      <Header title="Project Leads" subtitle={`${leads.length} request${leads.length === 1 ? "" : "s"} from your website`} />
      <div className="border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left font-mono-label text-[10px] text-zinc-500">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Source</th><th className="px-4 py-3 hidden md:table-cell">Email</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                <td className="px-4 py-3 font-medium text-zinc-900">{l.name}<div className="text-xs text-zinc-400 md:hidden">{l.email}</div></td>
                <td className="px-4 py-3 text-zinc-500">{l.source}</td>
                <td className="hidden px-4 py-3 text-zinc-500 md:table-cell">{l.email}</td>
                <td className="px-4 py-3"><span className={`px-2 py-1 text-[10px] font-semibold uppercase ${badge(l.status)}`}>{l.status}</span></td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setOpen(l)} data-testid={`view-lead-${l.id}`} className="text-xs font-semibold text-klein hover:underline">View</button>
                </td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-400">No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setOpen(null)}>
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto border border-zinc-200 bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
              <span className="font-mono-label text-[11px] text-zinc-500">Lead detail</span>
              <button onClick={() => setOpen(null)}><X className="h-5 w-5 text-zinc-400" /></button>
            </div>
            <div className="space-y-4 p-6">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Name" value={open.name} /><Info label="Company" value={open.company || "—"} />
                <Info label="Email" value={open.email} /><Info label="Phone" value={open.phone || "—"} />
              </div>
              <div>
                <span className="font-mono-label text-[10px] text-zinc-400">Project summary</span>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-zinc-700">{open.summary}</p>
              </div>
              <div className="flex items-center gap-2">
                {["new", "contacted", "closed"].map((s) => (
                  <button key={s} onClick={() => setStatus(open.id, s)} data-testid={`set-status-${s}`} className={`px-3 py-1.5 text-xs font-semibold ${open.status === s ? badge(s) : "border border-zinc-200 text-zinc-600"}`}>{s}</button>
                ))}
                <button onClick={() => del(open.id)} className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-accentred"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function Info({ label, value }) {
  return <div><span className="font-mono-label text-[10px] text-zinc-400">{label}</span><p className="text-zinc-800">{value}</p></div>;
}

/* ----------------------------- TOOLS ----------------------------- */
const EMPTY_TOOL = { name: "", category: "", description: "", price: "", image: "", badge: "", active: true, order: 0 };
function ToolsPanel() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => { api.get("/admin/tools").then((r) => setTools(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async (data) => {
    if (data.id) await api.put(`/admin/tools/${data.id}`, data);
    else await api.post("/admin/tools", data);
    setEditing(null); setLoading(true); load();
  };
  const del = async (id) => { if (!window.confirm("Delete this tool?")) return; await api.delete(`/admin/tools/${id}`); setTools((t) => t.filter((x) => x.id !== id)); };

  if (loading) return <Loader2 className="h-6 w-6 animate-spin text-klein" />;

  return (
    <div>
      <Header title="Software / Tools" subtitle="Manage products shown in the store"
        action={<button data-testid="add-tool-btn" onClick={() => setEditing(EMPTY_TOOL)} className="inline-flex items-center gap-2 bg-klein px-4 py-2.5 text-sm font-semibold text-white hover:bg-kleindark"><Plus className="h-4 w-4" /> Add tool</button>} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tools.map((t) => (
          <div key={t.id} data-testid={`admin-tool-${t.id}`} className="border border-zinc-200 bg-white">
            <div className="aspect-[16/9] overflow-hidden bg-zinc-100"><img src={t.image} alt={t.name} className="h-full w-full object-cover" /></div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <span className="font-mono-label text-[10px] text-zinc-400">{t.category}</span>
                {!t.active && <span className="text-[10px] font-semibold text-accentred">HIDDEN</span>}
              </div>
              <h3 className="mt-1 font-heading text-base font-semibold text-zinc-900">{t.name}</h3>
              <p className="mt-1 text-sm font-bold text-klein">{t.price}</p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setEditing(t)} data-testid={`edit-tool-${t.id}`} className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-1.5 text-xs font-semibold hover:bg-zinc-50"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                <button onClick={() => del(t.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-accentred"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <ToolForm initial={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ToolForm({ initial, onSave, onClose }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async (e) => { e.preventDefault(); setSaving(true); await onSave(f); setSaving(false); };
  return (
    <Modal title={f.id ? "Edit tool" : "Add tool"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name"><input data-testid="tool-form-name" required value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category"><input data-testid="tool-form-category" required value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls} /></Field>
          <Field label="Price"><input data-testid="tool-form-price" value={f.price} onChange={(e) => set("price", e.target.value)} className={inputCls} placeholder="$249" /></Field>
        </div>
        <Field label="Description"><textarea data-testid="tool-form-desc" required rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></Field>
        <Field label="Image URL"><input data-testid="tool-form-image" value={f.image} onChange={(e) => set("image", e.target.value)} className={inputCls} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Badge (optional)"><input value={f.badge} onChange={(e) => set("badge", e.target.value)} className={inputCls} placeholder="Popular" /></Field>
          <Field label="Order"><input type="number" value={f.order} onChange={(e) => set("order", Number(e.target.value))} className={inputCls} /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} /> Visible in store</label>
        <button type="submit" disabled={saving} data-testid="tool-form-save" className="flex w-full items-center justify-center gap-2 bg-klein px-6 py-3 text-sm font-semibold text-white hover:bg-kleindark disabled:opacity-50">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save tool</button>
      </form>
    </Modal>
  );
}

/* ----------------------------- SERVICES ----------------------------- */
const EMPTY_SVC = { title: "", short_desc: "", description: "", icon: "Cpu", image: "", features: [], order: 0, active: true };
const ICONS = ["Cpu", "ArrowRightLeft", "Headset", "Phone", "Code", "DatabaseBackup", "ShieldCheck", "GraduationCap", "Server"];
function ServicesPanel() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => { api.get("/services").then((r) => setServices(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async (data) => {
    const payload = { ...data, features: typeof data.features === "string" ? data.features.split("\n").filter(Boolean) : data.features };
    if (data.id) await api.put(`/admin/services/${data.id}`, payload);
    else await api.post("/admin/services", payload);
    setEditing(null); setLoading(true); load();
  };
  const del = async (id) => { if (!window.confirm("Delete this service?")) return; await api.delete(`/admin/services/${id}`); setServices((s) => s.filter((x) => x.id !== id)); };

  if (loading) return <Loader2 className="h-6 w-6 animate-spin text-klein" />;

  return (
    <div>
      <Header title="Services" subtitle="Manage the services shown across the site"
        action={<button data-testid="add-service-btn" onClick={() => setEditing(EMPTY_SVC)} className="inline-flex items-center gap-2 bg-klein px-4 py-2.5 text-sm font-semibold text-white hover:bg-kleindark"><Plus className="h-4 w-4" /> Add service</button>} />
      <div className="border border-zinc-200 bg-white">
        {services.map((s) => (
          <div key={s.id} data-testid={`admin-service-${s.id}`} className="flex items-center justify-between border-b border-zinc-100 px-4 py-3.5">
            <div>
              <p className="font-medium text-zinc-900">{s.title}</p>
              <p className="text-xs text-zinc-400">{s.short_desc}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing({ ...s, features: (s.features || []).join("\n") })} data-testid={`edit-service-${s.id}`} className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-1.5 text-xs font-semibold hover:bg-zinc-50"><Pencil className="h-3.5 w-3.5" /> Edit</button>
              <button onClick={() => del(s.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-accentred"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title={editing.id ? "Edit service" : "Add service"} onClose={() => setEditing(null)}>
          <ServiceForm initial={editing} onSave={save} />
        </Modal>
      )}
    </div>
  );
}
function ServiceForm({ initial, onSave }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async (e) => { e.preventDefault(); setSaving(true); await onSave(f); setSaving(false); };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label="Title"><input data-testid="service-form-title" required value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} /></Field>
      <Field label="Short description"><input data-testid="service-form-short" required value={f.short_desc} onChange={(e) => set("short_desc", e.target.value)} className={inputCls} /></Field>
      <Field label="Full description"><textarea required rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Icon"><select value={f.icon} onChange={(e) => set("icon", e.target.value)} className={inputCls}>{ICONS.map((i) => <option key={i}>{i}</option>)}</select></Field>
        <Field label="Order"><input type="number" value={f.order} onChange={(e) => set("order", Number(e.target.value))} className={inputCls} /></Field>
      </div>
      <Field label="Image URL"><input value={f.image} onChange={(e) => set("image", e.target.value)} className={inputCls} /></Field>
      <Field label="Features (one per line)"><textarea rows={4} value={f.features} onChange={(e) => set("features", e.target.value)} className={inputCls} /></Field>
      <button type="submit" disabled={saving} data-testid="service-form-save" className="flex w-full items-center justify-center gap-2 bg-klein px-6 py-3 text-sm font-semibold text-white hover:bg-kleindark disabled:opacity-50">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save service</button>
    </form>
  );
}

/* ----------------------------- CONTENT ----------------------------- */
function ContentPanel() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { api.get("/content").then((r) => setC({ stats: [], ...r.data })); }, []);
  if (!c) return <Loader2 className="h-6 w-6 animate-spin text-klein" />;
  const set = (k, v) => setC({ ...c, [k]: v });
  const setStat = (i, k, v) => { const s = [...(c.stats || [])]; s[i] = { ...s[i], [k]: v }; setC({ ...c, stats: s }); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setSaved(false);
    await api.put("/admin/content", c);
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="max-w-3xl">
      <Header title="Site Content" subtitle="Edit the hero and about sections" />
      <form onSubmit={save} className="space-y-4 border border-zinc-200 bg-white p-6">
        <Field label="Hero title"><input data-testid="content-hero-title" value={c.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} className={inputCls} /></Field>
        <Field label="Hero subtitle"><textarea rows={2} value={c.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} className={inputCls} /></Field>
        <Field label="About heading"><input value={c.about_heading || ""} onChange={(e) => set("about_heading", e.target.value)} className={inputCls} /></Field>
        <Field label="About body"><textarea rows={4} value={c.about_body || ""} onChange={(e) => set("about_body", e.target.value)} className={inputCls} /></Field>
        <Field label="Home AI assistant greeting"><textarea data-testid="content-home-greeting" rows={3} value={c.home_greeting || ""} onChange={(e) => set("home_greeting", e.target.value)} className={inputCls} placeholder="Hi! I'm here to help..." /></Field>
        <Field label="Let's build AI greeting"><textarea data-testid="content-build-greeting" rows={3} value={c.build_greeting || ""} onChange={(e) => set("build_greeting", e.target.value)} className={inputCls} /></Field>
        <Field label="WhatsApp number (with country code, digits only)"><input data-testid="content-whatsapp" value={c.whatsapp_number || ""} onChange={(e) => set("whatsapp_number", e.target.value)} className={inputCls} placeholder="50937209459" /></Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Get in touch — email"><input data-testid="content-contact-email" value={c.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className={inputCls} placeholder="sales@genesisitsolution.com" /></Field>
          <Field label="Get in touch — phone"><input data-testid="content-contact-phone" value={c.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className={inputCls} placeholder="+1 (800) 555-0142" /></Field>
        </div>
        <Field label="Get in touch — address (optional)"><textarea data-testid="content-contact-address" rows={2} value={c.contact_address || ""} onChange={(e) => set("contact_address", e.target.value)} className={inputCls} placeholder="123 Main St, City, Country" /></Field>
        <div>
          <span className="font-mono-label text-[10px] text-zinc-400">Stats</span>
          <div className="mt-2 grid grid-cols-2 gap-3">
            {(c.stats || []).map((st, i) => (
              <div key={i} className="grid grid-cols-2 gap-2">
                <input value={st.value} onChange={(e) => setStat(i, "value", e.target.value)} className={inputCls} placeholder="500+" />
                <input value={st.label} onChange={(e) => setStat(i, "label", e.target.value)} className={inputCls} placeholder="Projects" />
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving} data-testid="content-save" className="inline-flex items-center gap-2 bg-klein px-6 py-3 text-sm font-semibold text-white hover:bg-kleindark disabled:opacity-50">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save changes</button>
          {saved && <span className="text-sm font-semibold text-green-600">Saved!</span>}
        </div>
      </form>
    </div>
  );
}

/* ----------------------------- shared ----------------------------- */
const inputCls = "w-full border border-zinc-200 px-3 py-2.5 text-sm outline-none focus:border-klein";
function Field({ label, children }) {
  return <label className="block"><span className="font-mono-label text-[10px] text-zinc-400">{label}</span><div className="mt-1.5">{children}</div></label>;
}
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto border border-zinc-200 bg-white" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-4">
          <span className="font-mono-label text-[11px] text-zinc-500">{title}</span>
          <button onClick={onClose}><X className="h-5 w-5 text-zinc-400" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
